/**
 * Mixpanel on the website: the one place that talks to it.
 * The plan is docs/design/WEB-TRACKING-PLAN.md. No event and no property that is not in it.
 *
 *   kz.track(name, props)     one event of the plan. Every feature calls this, never `mixpanel`.
 *   kz.identify()             a logged-in customer is "shopify:<his id>"; nobody else is named.
 *   kz.registerContext()      what goes on every event (plan, section B), once per page.
 *   kz.seen(element, key, run)  runs once per page view, when the element has been at least
 *                             half on screen for one second (the "seen" events).
 *
 * WHAT IS NEVER SENT: a name, an email, a phone number. A search for something that looks like
 * one is sent as "[email]" or "[number]". Height and weight leave as 5 cm / 5 kg bands.
 *
 * LOADING. Mixpanel's own library comes from its CDN through its official loader, once the page
 * has been drawn: nothing here stands between him and the page. What is tracked before it
 * arrives waits in a list (snippets/kz-track.liquid) and is sent when it does.
 *
 * A THEME THAT IS NOT THE LIVE ONE reports with `is_test_build: true`, and on it (or with
 * `?mp_debug=1` in the address) anything outside the plan is named in the console.
 *
 * THE CHECKOUT is Shopify's: its events come from extras/mixpanel-checkout-pixel.js. That pixel
 * finds the visitor by two notes this file leaves on the bag, `_mp_id` and `_mp_role`.
 */

// ─── The plan (section C), as a list a script can check against ───────────────

export const PLAN = {
  'Product Viewed': ['product_handle', 'product_title', 'price', 'currency', 'is_available', 'category', 'fit', 'has_fit_table', 'source'],
  'Collection Viewed': ['collection_handle', 'product_count', 'source', 'nav_docked'],
  'Collection Show More Tapped': ['collection_handle', 'shown_before'],
  'Search Performed': ['query', 'results_count'],
  'Search Result Tapped': ['query', 'position', 'product_handle'],
  'Search Failed': ['error'],
  'OOS Size Tapped': ['product_handle', 'size', 'variant_id'],
  'Find My Size Opened': ['product_handle', 'has_profile'],
  'Find My Size Completed': ['product_handle', 'result', 'size', 'preference', 'height_band', 'weight_band', 'differs_from_last'],
  'Find My Size Abandoned': ['step'],
  'Size Changed After Recommendation': ['product_handle', 'recommended', 'chosen'],
  'Add to Cart': ['product_handle', 'product_title', 'variant_id', 'size', 'color', 'price', 'quantity', 'currency', 'source', 'size_source'],
  'Add to Cart Failed': ['reason', 'error', 'source'],
  'Cart Viewed': ['item_count', 'subtotal', 'currency', 'free_delivery_remaining', 'source'],
  'Cart Item Removed': ['product_handle', 'size', 'price'],
  'Cart Size Changed': ['product_handle', 'from', 'to', 'had_recommendation'],
  'Recommendation Shown': ['job', 'count', 'reasons', 'product_handle'],
  'Recommendation Clicked': ['job', 'reason', 'position', 'product_handle'],
  'Recommendations Failed': ['reason'],
  'Worn With Seen': ['product_handle', 'pieces_count', 'origin'],
  'Worn With Added': ['product_handle', 'pieces_added', 'value_added', 'full_look'],
  'Notify Me Modal Opened': ['product_handle', 'size'],
  'Notify Me Subscribed': ['product_handle', 'size', 'variant_id', 'channel'],
  'Notify Me Failed': ['reason'],
  'App Banner Seen': ['placement'],
  'App Banner Tapped': ['placement'],
  'App Banner Dismissed': ['placement'],
  'Language Switched': ['from', 'to'],
  'WhatsApp Tapped': ['placement'],
  'Checkout Started': ['item_count', 'subtotal', 'currency'],
  'Room Tile Tapped': ['placement', 'piece_id', 'piece_title'],
  'Room Entered': [],
  'Piece Viewed': ['piece_id', 'piece_title', 'view_mode'],
  'Size Reserved': ['piece_id', 'piece_title', 'color', 'size', 'view_mode', 'position', 'kept_on_phone'],
  'Reserve Cancelled': ['piece_id', 'piece_title', 'view_mode', 'position'],
  'Piece Liked': ['piece_id', 'piece_title', 'view_mode', 'position', 'color'],
  'Piece Unliked': ['piece_id', 'piece_title', 'view_mode', 'position', 'color'],
  'Not For Me': ['piece_id', 'piece_title', 'reason', 'view_mode', 'position'],
  'Room Closed Seen': [],
  'Piece Impression': ['piece_id', 'piece_title', 'position', 'view_mode', 'dwell_ms'],
  'Piece Image Swiped': ['piece_id', 'piece_title', 'image_index', 'image_count', 'position', 'view_mode'],
  'Reserve Sheet Opened': ['piece_id', 'piece_title', 'position', 'view_mode'],
  'Room View Changed': ['view_mode'],
  'Piece Colour Chosen': ['piece_id', 'piece_title', 'color', 'view_mode', 'position'],
  'Room Done Opened': ['reserved', 'liked', 'saved'],
  'Room Phone Submitted': ['step', 'country_code', 'code_sent', 'reason'],
  'Room Code Verified': ['step'],
  'Room Picks Saved': ['step', 'reserved', 'liked', 'skipped'],
};

// How the Winter27 samples were being looked at (the same three words as the app).
const VIEW_MODES = ['reel', 'cards', 'detail', 'rail'];
// Where the WhatsApp number was asked: at the first reserve, for picks kept on the phone from before, or on the "Done" screen.
const ROOM_STEPS = ['first_reserve', 'old_picks', 'done'];

/** Where the plan names the values a property may take. A "*" ends a family ("recs_similar"). */
export const VALUES = {
  'Product Viewed': { source: ['collection', 'search', 'recs_similar', 'recs_complete', 'recs_recent', 'recs_discover', 'home', 'cart_upsell', 'direct'] },
  'Collection Viewed': { source: ['category_bar', 'menu', 'home_tile', 'link', 'direct'] },
  'Find My Size Completed': { result: ['ok', 'out_of_range'], preference: ['closer', 'as_designed', 'looser'] },
  'Find My Size Abandoned': { step: ['height', 'weight', 'fit'] },
  'Add to Cart': { source: ['product_page', 'sticky_bar', 'quick_add', 'complete_look', 'cart_upsell', 'recs_*'], size_source: ['manual', 'find_my_size', 'url', 'single_size'] },
  'Add to Cart Failed': { reason: ['sold_out', 'limit', 'network', 'other'], source: ['product_page', 'sticky_bar', 'quick_add', 'complete_look', 'cart_upsell', 'recs_*'] },
  'Cart Viewed': { source: ['confirmation', 'icon', 'checkout_link', 'page'] },
  'Worn With Seen': { origin: ['look', 'complement', 'shopify'] },
  'Notify Me Subscribed': { channel: ['whatsapp'] },
  'Notify Me Failed': { reason: ['invalid_phone', 'network', 'server', 'rate_limited'] },
  'App Banner Seen': { placement: ['top_bar', 'footer', 'empty_bag', 'home_band'] },
  'App Banner Tapped': { placement: ['top_bar', 'footer', 'empty_bag', 'home_band'] },
  'App Banner Dismissed': { placement: ['top_bar', 'footer', 'empty_bag', 'home_band'] },
  'WhatsApp Tapped': { placement: ['footer', 'fms_out_of_range', 'cart', 'page'] },
  'Not For Me': { reason: ['price', 'colour', 'fit', 'style'], view_mode: VIEW_MODES },
  'Piece Viewed': { view_mode: VIEW_MODES },
  'Size Reserved': { view_mode: VIEW_MODES },
  'Reserve Cancelled': { view_mode: VIEW_MODES },
  'Piece Liked': { view_mode: VIEW_MODES },
  'Piece Unliked': { view_mode: VIEW_MODES },
  'Piece Impression': { view_mode: VIEW_MODES },
  'Piece Image Swiped': { view_mode: VIEW_MODES },
  'Reserve Sheet Opened': { view_mode: VIEW_MODES },
  'Room View Changed': { view_mode: ['reel', 'cards'] },
  'Piece Colour Chosen': { view_mode: VIEW_MODES },
  'Room Tile Tapped': { placement: ['home_hero', 'home_band', 'home_rail', 'top_bar', 'menu', 'link'] },
  'Room Phone Submitted': { step: ROOM_STEPS },
  'Room Code Verified': { step: ROOM_STEPS },
  'Room Picks Saved': { step: [...ROOM_STEPS, 'return'] },
};

/**
 * What is wrong with an event, measured against the plan. An empty list means nothing is.
 * @param {string} name  @param {object} [props]  @returns {string[]}
 */
export function problems(name, props = {}) {
  const allowed = PLAN[name];
  if (!allowed) return [`"${name}" is not an event of the tracking plan`];
  const found = [];
  for (const [key, value] of Object.entries(props)) {
    if (!allowed.includes(key)) {
      found.push(`"${name}" has no property "${key}" in the tracking plan`);
      continue;
    }
    const values = VALUES[name]?.[key];
    if (!values || value === undefined || value === null) continue;
    const fits = values.some((known) => (known.endsWith('*') ? String(value).startsWith(known.slice(0, -1)) : known === value));
    if (!fits) found.push(`"${name}": ${key} is "${value}", which the tracking plan does not list`);
  }
  return found;
}

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/;
// A phone number: one written with its country code, eight or more digits standing on their own,
// or two groups of four (the way a Kuwaiti number is written). A year, a job number or a size is none of these.
const PHONE = /(?:\+|\b00)\d[\d\s-]{6,}\d|(?<![\w-])\d{8,15}(?![\w-])|(?<![\w-])\d{4}[\s-]\d{4}(?![\w-])/;

/** A height or a weight as the 5-wide band it falls in: 173 → "170-174". Never the number itself. */
export function band(value, width = 5) {
  const number = Number(value);
  if (!Number.isFinite(number)) return undefined;
  const from = Math.floor(number / width) * width;
  return `${from}-${from + width - 1}`;
}

/** What he searched for, as the plan wants it: lower case, 40 characters at most, and never an email or a phone number. */
export function searchWords(typed) {
  const text = String(typed ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (EMAIL.test(text)) return '[email]';
  if (PHONE.test(text)) return '[number]';
  return text.slice(0, 40);
}

/** The last line of defence: a value that holds an email or a phone number does not leave. */
export function safe(value) {
  if (Array.isArray(value)) return value.map(safe);
  if (typeof value !== 'string') return value;
  if (EMAIL.test(value) || PHONE.test(value)) return '[removed]';
  return value.length > 255 ? value.slice(0, 255) : value;
}

/** An event's properties as they are sent: nothing empty, nothing private. */
export function tidy(props = {}) {
  const out = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = safe(value);
  }
  return out;
}

/** Where a product link stands, in the plan's words for `Product Viewed.source`. */
export function entryOf({ job, inBag, inSearch, pageType }) {
  if (inBag) return 'cart_upsell';
  if (job) return { similar: 'recs_similar', complete: 'recs_complete', recent: 'recs_recent' }[job] || 'recs_discover';
  if (inSearch || pageType === 'search') return 'search';
  if (pageType === 'collection') return 'collection';
  if (pageType === 'home') return 'home';
  return 'direct';
}

// ─── The page part (the tests read only what is above) ───────────────────────

const LIBRARY = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
const FROM_KEY = 'kz-from';
const ID_KEY = 'kz-mp-noted';
/** A tap on a link counts as the way into the next page for this long. */
const FROM_TIME = 60 * 1000;

const kept = (run, otherwise = null) => {
  try {
    return run();
  } catch (error) {
    return otherwise;
  }
};

function start(config) {
  const kz = (window.kz = window.kz || {});
  const context = config.context;
  const debug = new URLSearchParams(window.location.search).get('mp_debug') === '1';
  const strict = debug || context.is_test_build;

  // ── Mixpanel's own loader (github.com/mixpanel/mixpanel-js, src/loaders/mixpanel-jslib-snippet.js),
  // unchanged but for one thing: the library itself is asked for after the page has been drawn.
  const mixpanel = (window.mixpanel = window.mixpanel || []);
  if (!mixpanel.__SV) {
    mixpanel._i = [];
    mixpanel.init = function (token, settings, name) {
      let target = mixpanel;
      if (typeof name !== 'undefined') target = mixpanel[name] = [];
      else name = 'mixpanel';
      target.people = target.people || [];
      target.toString = (noStub) => `mixpanel${name !== 'mixpanel' ? `.${name}` : ''}${noStub ? '' : ' (stub)'}`;
      target.people.toString = () => `${target.toString(1)}.people (stub)`;
      const defer = (fn) => {
        const parts = fn.split('.');
        const on = parts.length === 2 ? target[parts[0]] : target;
        const method = parts[parts.length - 1];
        on[method] = function () {
          on.push([method].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      'disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders start_session_recording stop_session_recording people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove'
        .split(' ')
        .forEach(defer);
      mixpanel._i.push([token, settings, name]);
    };
    mixpanel.__SV = 1.2;
    const fetchLibrary = () => {
      if (fetchLibrary.done) return;
      fetchLibrary.done = true;
      const script = Object.assign(document.createElement('script'), { src: LIBRARY, async: true });
      document.head.append(script);
    };
    // Once the page has been drawn: the frame after this script runs, which is after the page's
    // own content is in. Not later than that: what is tracked before the library arrives waits
    // in its list, and is lost if he leaves the page first. (A tab in the background draws
    // nothing: three seconds at the latest.)
    window.requestAnimationFrame(() => window.setTimeout(fetchLibrary, 0));
    window.setTimeout(fetchLibrary, 3000);
  }

  // ── What goes on every event, and who he is ──
  // Always `window.mixpanel`, read at the moment of the call: when the library arrives it puts
  // itself there in place of the loader above, and a name kept from before would go nowhere.

  kz.registerContext = () => window.mixpanel.register({ ...context }, { persistent: false });
  kz.identify = () => {
    if (config.customer) window.mixpanel.identify(`shopify:${config.customer}`);
  };

  /**
   * The two notes on the bag for Shopify's checkout, which this file cannot reach: who he is
   * to Mixpanel, and whether this theme is the live one. Written once the bag holds something,
   * and again whenever either changes. Nothing else about the bag is touched.
   */
  let distinct = '';
  let pieces = null;
  function noteOnBag() {
    if (!distinct || !pieces) return;
    const note = `${distinct}|${context.theme_role}`;
    if (kept(() => sessionStorage.getItem(ID_KEY)) === note) return;
    kept(() => sessionStorage.setItem(ID_KEY, note));
    fetch(config.cartUpdate.replace(/(\.js)?$/, '.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _mp_id: distinct, _mp_role: context.theme_role } }),
      keepalive: true,
    }).catch(() => kept(() => sessionStorage.removeItem(ID_KEY)));
  }
  // The bag's own copy first: it is the one that knows the free-delivery amount.
  const bagNow = () => kept(() => JSON.parse((document.querySelector('cart-items script[data-bag-state]') || document.querySelector('script[data-bag-state]')).textContent));
  document.addEventListener('cart:updated', (event) => {
    pieces = Number(event.detail?.cart?.item_count) || 0;
    // An empty bag is a new bag next time: the notes are written again.
    if (!pieces) kept(() => sessionStorage.removeItem(ID_KEY));
    noteOnBag();
  });

  mixpanel.init(config.token, {
    // The page view is sent below, by Mixpanel's own track_pageview(), once what goes on every
    // event is registered: sent from inside init it would leave without `page_type`.
    track_pageview: false,
    autocapture: false,
    // The share of visits recorded as replays: Theme settings > Mixpanel.
    record_sessions_percent: Math.min(100, Math.max(0, Number(config.replay) || 0)),
    record_mask_text_selector: '[data-mp-mask]',
    persistence: 'localStorage',
    debug,
    loaded: (library) => {
      distinct = library.get_distinct_id();
      pieces = Number(bagNow()?.count) || 0;
      noteOnBag();
    },
  });
  kz.registerContext();
  kz.identify();
  window.mixpanel.track_pageview();

  // ── The wrapper ──

  kz.track = (name, props = {}, how = {}) => {
    if (strict) for (const problem of problems(name, props)) console.warn(`Kanze tracking: ${problem}.`);
    const sent = tidy(props);
    // `leaving`: the page is about to change, so the event goes at once and survives it.
    window.mixpanel.track(name, sent, how?.leaving ? { transport: 'sendBeacon', send_immediately: true } : undefined);
    // For the checks, and for anyone looking: what was tracked, as it left.
    document.dispatchEvent(new CustomEvent('kz:track', { detail: { name, props: sent } }));
  };
  for (const [name, props, how] of kz.q || []) kz.track(name, props, how);
  kz.q = [];

  // ── "Seen": half on screen for a second, once per page view ──

  const fired = new Set();
  const waiting = new WeakMap();
  const timers = new WeakMap();
  const watcher =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const job = waiting.get(entry.target);
              if (!job) continue;
              // Half of it, or (for something taller than the screen) half of the screen.
              const half = 0.5 * Math.min(entry.boundingClientRect.height, entry.rootBounds?.height || window.innerHeight);
              const onShow = entry.isIntersecting && half > 0 && entry.intersectionRect.height >= half - 1 && entry.intersectionRect.width > 0;
              if (!onShow) {
                window.clearTimeout(timers.get(entry.target));
                timers.delete(entry.target);
              } else if (!timers.has(entry.target)) {
                timers.set(
                  entry.target,
                  window.setTimeout(() => {
                    watcher.unobserve(entry.target);
                    waiting.delete(entry.target);
                    if (fired.has(job.key)) return;
                    fired.add(job.key);
                    kept(job.run);
                  }, 1000)
                );
              }
            }
          },
          { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
        )
      : null;
  kz.seen = (element, key, run) => {
    if (!watcher || !element || fired.has(key) || waiting.has(element)) return;
    waiting.set(element, { key, run });
    watcher.observe(element);
  };

  // ── What only needs the page: views, and taps on plain links ──

  const page = context.page_type;
  const closest = (event, selector) => (event.target instanceof Element ? event.target.closest(selector) : null);
  const handleOf = (address) => kept(() => decodeURIComponent(new URL(address, window.location.origin).pathname.match(/\/products\/([^/?#]+)/)?.[1] || ''), '');
  const pathOf = (address) => kept(() => new URL(address, window.location.origin).pathname.replace(/^\/[a-z]{2}(?=\/)/, ''), '');

  // The way into the next page is the link that was tapped here. Heard last of all, so that a
  // tap some script answered itself (the bag icon opens the drawer, a category changes in
  // place) is not taken for a way into another page.
  let cameBy = 'direct';
  window.addEventListener(
    'click',
    (event) => {
      const link = closest(event, 'a[href]');
      if (!link || link.target === '_blank' || event.defaultPrevented) return;
      let note = null;
      const product = handleOf(link.href);
      if (product) {
        const job = link.closest('complete-look') ? 'complete' : link.closest('recs-rail')?.dataset.job;
        // Another colour of the piece he is looking at: he came the way he came.
        const by = link.closest('[data-sibling], .picker') && page === 'product' ? cameBy : entryOf({ job, inBag: Boolean(link.closest('bag-upsell')), inSearch: Boolean(link.closest('predictive-search')), pageType: page });
        note = { p: product, s: by };
      } else if (pathOf(link.href) === '/cart') {
        note = { b: link.closest('bag-added') ? 'confirmation' : link.closest('.header') ? 'icon' : 'checkout_link' };
      } else if (pathOf(link.href).startsWith('/collections/')) {
        const by = link.closest('[data-category-bar]') ? 'category_bar' : link.closest('.header, menu-drawer, header-nav') ? 'menu' : page === 'home' ? 'home_tile' : 'link';
        note = { c: pathOf(link.href), s: by };
      }
      if (note) kept(() => sessionStorage.setItem(FROM_KEY, JSON.stringify({ ...note, t: Date.now() })));
    }
  );
  const from = kept(() => JSON.parse(sessionStorage.getItem(FROM_KEY)));
  const fresh = from && Date.now() - from.t < FROM_TIME ? from : null;
  kept(() => sessionStorage.removeItem(FROM_KEY));

  // Once every script of the page has run (this one runs first): they know things this one asks about.
  const whenReady = (run) => (document.readyState === 'complete' ? run() : document.addEventListener('DOMContentLoaded', run, { once: true }));
  whenReady(() => {
    if (config.product) {
      cameBy = fresh?.p === config.product.product_handle ? fresh.s : 'direct';
      kz.track('Product Viewed', {
        ...config.product,
        price: window.Kanze?.amount ? window.Kanze.amount(config.product.price) : undefined,
        // Find my size takes itself off the page when this piece has no table it can use.
        has_fit_table: Boolean(document.querySelector('find-my-size [data-fms-finder]')),
        source: cameBy,
      });
    }

    const collection = document.querySelector('collection-page[data-handle]');
    if (collection) {
      kz.track('Collection Viewed', {
        collection_handle: collection.dataset.handle,
        product_count: Number(collection.dataset.count) || 0,
        source: fresh?.c === pathOf(window.location.href) ? (fresh.s === 'category_bar' ? 'link' : fresh.s) : 'direct',
        nav_docked: false,
      });
    }

    // The bag as a page of its own. As a drawer it is tracked where it opens (assets/cart.js).
    if (page === 'cart') kz.bagViewed(fresh?.b || 'page');

    banners();
  });

  /** `Cart Viewed`, with what the bag holds at that moment. */
  kz.bagViewed = (source) => {
    const bag = bagNow();
    if (!bag || !window.Kanze?.amount) return;
    kz.track('Cart Viewed', {
      item_count: bag.count,
      subtotal: window.Kanze.amount(bag.total),
      free_delivery_remaining: bag.threshold ? window.Kanze.amount(Math.max(0, bag.threshold - bag.total)) : undefined,
      source,
    });
  };

  // The app banners: the 5 KD bar, the code in the footer, the line in an empty bag, the band on the home page.
  function banners() {
    for (const banner of document.querySelectorAll('[data-app-banner]')) {
      const placement = banner.dataset.appBanner;
      kz.seen(banner, `app:${placement}`, () => kz.track('App Banner Seen', { placement }));
    }
  }
  document.addEventListener('cart:updated', () => window.requestAnimationFrame(banners));

  document.addEventListener('click', (event) => {
    const banner = closest(event, '[data-app-banner]');
    if (banner && closest(event, '[data-dismiss]')) kz.track('App Banner Dismissed', { placement: banner.dataset.appBanner });
    else if (banner && closest(event, 'a[href]')) kz.track('App Banner Tapped', { placement: banner.dataset.appBanner }, { leaving: true });

    // A way into the pre-order page (Theme settings > Mixpanel > Pre-order page): same name as the app's home tile.
    // The home row's cards open a piece by script and are counted there (assets/selection-room.js).
    const toRoom = config.room_page && closest(event, 'a[href]');
    if (toRoom && !toRoom.closest('[data-room-action]') && pathOf(toRoom.href) === pathOf(config.room_page) && pathOf(window.location.href) !== pathOf(config.room_page)) {
      const placement = toRoom.closest('selection-room') ? 'home_rail' : toRoom.closest('announcement-bar') ? 'top_bar' : toRoom.closest('.home-hero') ? 'home_hero' : toRoom.closest('.banner') && page === 'home' ? 'home_band' : toRoom.closest('.header, menu-drawer, header-nav') ? 'menu' : 'link';
      kz.track('Room Tile Tapped', { placement }, { leaving: true });
    }

    const whatsapp = closest(event, 'a[href*="wa.me/"], a[href*="api.whatsapp.com/"], a[href^="whatsapp:"]');
    if (whatsapp) {
      const placement = whatsapp.closest('find-my-size') ? 'fms_out_of_range' : whatsapp.closest('cart-items') ? 'cart' : whatsapp.closest('footer, .footer, .shopify-section-group-footer-group') ? 'footer' : 'page';
      kz.track('WhatsApp Tapped', { placement }, { leaving: true });
    }
  });

  // Checkout pressed, in the bag or in the "added" confirmation; and the language, a form
  // Shopify answers by sending him to the same page in the other language.
  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (event.submitter?.name === 'checkout') {
      const bag = bagNow();
      if (bag && window.Kanze?.amount) kz.track('Checkout Started', { item_count: bag.count, subtotal: window.Kanze.amount(bag.total) }, { leaving: true });
      return;
    }
    const chosen = event.submitter?.name === 'locale_code' ? event.submitter.value : form?.elements.locale_code?.value;
    if (!form || !chosen || chosen === context.locale) return;
    kz.track('Language Switched', { from: context.locale, to: chosen }, { leaving: true });
  });
}

const config = typeof document === 'undefined' ? null : kept(() => JSON.parse(document.getElementById('KzTrack').textContent));
if (config?.token) start(config);
